import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { LockIcon, CheckIcon, AlertCircleIcon } from './Icons';

export default function PasswordManager({ currentUser, onSuccess }) {
  const [hasPassword, setHasPassword] = useState(currentUser?.has_password || false);
  const [oldPassword, setOldPassword] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  
  const needsUsername = !hasPassword && !currentUser?.email && !currentUser?.phone && !currentUser?.username;
  const [username, setUsername] = useState('');
  
  const [usernameAvailability, setUsernameAvailability] = useState({ checking: false, available: null, message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Debounced username availability check
  useEffect(() => {
    if (!needsUsername) {
      setUsernameAvailability({ checking: false, available: null, message: '' });
      return;
    }
    const trimmed = (username || '').trim();
    if (!trimmed || trimmed === (currentUser?.username || '')) {
      setUsernameAvailability({ checking: false, available: null, message: '' });
      return;
    }

    setUsernameAvailability({ checking: true, available: null, message: 'Checking availability...' });
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/auth/check-username?username=${encodeURIComponent(trimmed)}`);
        setUsernameAvailability({
          checking: false,
          available: !!res.data?.available,
          message: res.data?.message || '',
        });
      } catch (err) {
        setUsernameAvailability({
          checking: false,
          available: false,
          message: err.response?.data?.message || 'Error checking username.',
        });
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [username, needsUsername, currentUser?.username]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== passwordConfirmation) {
      setError('Passwords do not match');
      return;
    }
    
    if (needsUsername && usernameAvailability.available === false) {
      setError('Please choose an available username');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        password,
        password_confirmation: passwordConfirmation,
      };
      
      if (hasPassword) {
        payload.current_password = oldPassword;
      }
      
      if (needsUsername) {
        payload.username = username;
      }
      
      const res = await api.post('/users/me/security/change-password', payload);
      setSuccess(res.data?.message || 'Password updated successfully');
      setHasPassword(true);
      setOldPassword('');
      setPassword('');
      setPasswordConfirmation('');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to update password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-white dark:bg-slate-900/60 p-6 rounded-xl border border-slate-200 dark:border-slate-700 mb-6 shadow-xl">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white">{hasPassword ? 'Change Password' : 'Set Password'}</h2>
        <span className="inline-block px-3 py-1 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-xs font-medium rounded border border-indigo-300 dark:border-indigo-700">
          Security
        </span>
      </div>
      
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
        {hasPassword 
          ? 'Update your password to keep your account secure.' 
          : 'Set a password to enable email/username and password login.'}
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
          <AlertCircleIcon className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-2">
          <CheckIcon className="w-5 h-5 flex-shrink-0" />
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
        {needsUsername && (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Set a Username <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Since you don't have an email or phone, you must choose a username to sign in with your new password.</p>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className={`w-full bg-slate-50 dark:bg-slate-800 border rounded-lg px-4 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-colors ${
                usernameAvailability.available === false ? 'border-red-500 focus:border-red-500' : 'border-slate-300 dark:border-slate-600'
              }`}
            />
            {usernameAvailability.checking && <p className="text-xs text-blue-500 mt-1">{usernameAvailability.message}</p>}
            {!usernameAvailability.checking && usernameAvailability.available === true && <p className="text-xs text-emerald-500 mt-1">{usernameAvailability.message || 'Username is available'}</p>}
            {!usernameAvailability.checking && usernameAvailability.available === false && username && <p className="text-xs text-red-500 mt-1">{usernameAvailability.message}</p>}
          </div>
        )}

        {hasPassword && (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Current Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <LockIcon className="h-4 w-4 text-slate-400" />
              </div>
              <input
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                required
                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg pl-10 pr-4 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-colors"
              />
            </div>
          </div>
        )}
        
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            New Password <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <LockIcon className="h-4 w-4 text-slate-400" />
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg pl-10 pr-4 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-colors"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Confirm New Password <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <LockIcon className="h-4 w-4 text-slate-400" />
            </div>
            <input
              type="password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              required
              minLength={8}
              className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg pl-10 pr-4 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-colors"
            />
          </div>
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={submitting || (needsUsername && usernameAvailability.available === false)}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Saving...' : (hasPassword ? 'Update Password' : 'Set Password')}
          </button>
        </div>
      </form>
    </section>
  );
}
